import { useEffect, useState } from "react";
import {
  LoginRetryState,
  copyTopBarStatus,
  gateAction,
  isLiveHeader,
  useSettings,
  useLinkGroups,
  useTrading,
  type ConnectionView,
  type Me,
} from "@helm/core";
import { marketPhase } from "@helm/core/bars";
import { UNSTABLE_RTT_MS, latencyText, networkStatus, sessionTag, venueOf } from "@helm/ui";
import { onOpenLockoutPanel } from "./lockoutEvents";
import { settingsSync } from "../session";
import { usePickerView } from "../viewmodel/account";
import { usePopover } from "../components/Popover";
import { useMediaQuery } from "../components/useMediaQuery";
import { Icon } from "../components/Icon";
import { Brand } from "../components/Brand";
import { LockoutPanel } from "./LockoutPanel";
import { SimBadge } from "../trading/SimBadge";
import { OneClickToggle } from "../trading/OneClickToggle";
import { useGateContext } from "../trading/control";
import { TopBarClock } from "./TradovateTopBar";
import { RithmicAccountControl } from "./RithmicAccount";
import { ConnectionItem } from "./ConnectionItem";
import { openCopyTrader } from "./topbarDialogs";
import { toggleChartOnly, useChartOnly } from "../workspace/chartOnly";
import "./topbarMenus.css";
import "./statusLine.css";
import { SignOutButton } from "../auth/SignOutButton";

/**
 * The desk's status line (owner 2026-10-04 UI clean-up; it replaces the top bar and the bottom
 * status bar): every non-trading value, status or settings entry appears once, here —
 *
 *   TradePrizm · Rithmic ● Connected · account picker · trading state · 1-click on/off · Hotkeys on ·
 *   … · CME · RTH open · 18 ms · clock (CT) · Copy · Chart only · Privacy · Lockout · Settings ·
 *   More · account
 *
 * The connection item is the one place connection shows (its menu: the reconnect detail, the
 * Rithmic login dialog, Diagnostics); the account picker ("Trading account") is the trading account
 * for every panel and what the position strip shows ("All accounts" shows totals; orders need one
 * account); picking an account in any panel's account picker changes it here too; the
 * trading badge is the one trading state. Account figures and the account-level Flatten are the
 * position strip under it (PositionStrip.tsx).
 *
 * Fit (1280 px in every style): Flatten (in the strip), the trading state, the connection, Copy,
 * Chart only, Settings, the account menu and the one-click switch never fold (up to 1440 px Chart
 * only drops its icon); below 1300 px the hotkeys and session tags fold into "More" (measured
 * with e2e/topbar-fit-preview.mjs), and so they do while a refused login's red retry line shows
 * (ConnectionItem: it takes the room they leave). The one-click switch is the module badge the
 * order surfaces dropped (OneClickToggle, same first-time explanation): always shown as
 * "1-click on" / "1-click off" and flipped with one click.
 * A LIVE account or LIVE order plant draws a red rule under the line.
 */
export function TopBar({
  me,
  connection,
  onOpenPalette,
  onOpenSettings,
}: {
  me: Me;
  connection: ConnectionView;
  onOpenPalette: () => void;
  onOpenSettings: () => void;
}): React.JSX.Element {
  const view = usePickerView();
  const privacy = useSettings((s) => s.settings.privacy);
  const sessionEnv = useTrading((t) => t.session?.environment);
  const live = isLiveHeader(sessionEnv, view.allSelected ? null : (view.active?.environment ?? null));
  const narrow = useMediaQuery("(max-width: 1440px)");
  // A refused login's red retry line (owner decision 2026-10-04) gets the tags' room.
  const refused =
    useTrading(
      (t) => (t.login?.retry?.state ?? LoginRetryState.UNSPECIFIED) !== LoginRetryState.UNSPECIFIED,
    ) && connection.status === "connected";
  const tight = useMediaQuery("(max-width: 1300px)") || refused;
  const hotkeys = useHotkeys();
  const session = useSessionTag();
  return (
    <header
      className={live ? "topbar statusline topbar-live" : "topbar statusline"}
      aria-label="Status line"
      data-live={live ? "1" : undefined}
    >
      <Brand />
      <ConnectionItem connection={connection} />
      <div className="rithmic-group" role="group" aria-label="Account and trading state">
        <RithmicAccountControl view={view} privacy={privacy} showLogin={false} />
        <SimBadge compact={narrow} />
      </div>
      <OneClickToggle statusLine />
      {!tight && (
        <span
          className="statusline-tag"
          data-tag="hotkeys"
          data-on={hotkeys.on ? "1" : "0"}
          title={hotkeys.title}
        >
          {hotkeys.label}
        </span>
      )}
      <span className="spacer" />
      {!tight && (
        <span className="statusline-tag" data-tag="session" title={session.title}>
          {session.label}
        </span>
      )}
      <Latency connection={connection} />
      <TopBarClock />
      <CopyButton />
      <ChartOnlyButton />
      <PrivacyButton privacy={privacy} />
      <LockoutButton lockedUntil={view.lockedUntil} />
      <button
        type="button"
        className="statusline-btn"
        aria-label="Settings"
        title="Settings"
        onClick={onOpenSettings}
      >
        <Icon name="gear" size={13} />
        <span className="statusline-btn-word">Settings</span>
      </button>
      <MoreMenu foldTags={tight} hotkeys={hotkeys} session={session} onOpenPalette={onOpenPalette} />
      <AccountMenu me={me} />
    </header>
  );
}

type Tag = { label: string; title: string; on: boolean };

/** SPEC §9.8: hotkeys are live when order entry is (mock / Rithmic Test, READY). */
function useHotkeys(): Tag {
  const g = gateAction(useGateContext(), { kind: "flattenAll" });
  return g.ok
    ? {
        on: true,
        label: "Hotkeys on",
        title:
          "Trading hotkeys B S J A R F C X are live in a focused DOM ladder or order pad; Ctrl+Shift+F = Exit all accts (asks first when 'Ask before Exit All' is on)",
      }
    : { on: false, label: "Hotkeys off", title: `Trading hotkeys off — ${g.reason}` };
}

/** "CME · RTH open" for the active link symbol's exchange; the phase re-checks every 30 s. */
function useSessionTag(): Tag {
  const red = useLinkGroups((s) => s.groups.red.symbol);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => {
      setNow(Date.now());
    }, 30_000);
    return () => {
      clearInterval(t);
    };
  }, []);
  const phase = marketPhase(now);
  return {
    on: phase !== "closed",
    label: sessionTag(venueOf(red), phase),
    title: "CME futures session (charts show every hour): RTH 08:30–15:00 CT, break 16:00–17:00 CT",
  };
}

/** One latency number: the browser ↔ gateway round trip (the rest is in Diagnostics). */
function Latency({ connection }: { connection: ConnectionView }): React.JSX.Element {
  const ms = connection.status === "connected" ? connection.gwRttMs : null;
  const slow = ms !== null && ms > UNSTABLE_RTT_MS;
  return (
    <span
      className={slow ? "statusline-latency num status-warn" : "statusline-latency num"}
      title={`Browser ↔ gateway round trip${ms === null ? " (not measured)" : ""}. More under Diagnostics (connection menu, or Settings › Connection).`}
    >
      {latencyText(ms)}
    </span>
  );
}

function PrivacyButton({ privacy }: { privacy: boolean }): React.JSX.Element {
  return (
    <button
      type="button"
      className={privacy ? "statusline-btn active" : "statusline-btn"}
      aria-pressed={privacy}
      title={privacy ? "Privacy mask on — values show ***" : "Privacy mask off"}
      onClick={() => {
        void settingsSync.update({ privacy: !privacy });
      }}
    >
      <Icon name={privacy ? "eyeOff" : "eye"} size={13} />
      <span className="statusline-btn-word">Privacy</span>
    </button>
  );
}

/**
 * "More ⋯": the command palette, and below 1300 px the hotkeys and session tags that
 * fold out of the line.
 */
function MoreMenu({
  foldTags,
  hotkeys,
  session,
  onOpenPalette,
}: {
  foldTags: boolean;
  hotkeys: Tag;
  session: Tag;
  onOpenPalette: () => void;
}): React.JSX.Element {
  const pop = usePopover();
  return (
    <div className="popover-anchor">
      <button
        type="button"
        className="statusline-btn"
        aria-haspopup="dialog"
        aria-label="More"
        title="More: command palette"
        {...pop.triggerProps}
      >
        <span className="statusline-btn-word">More</span>
        <Icon name="more" size={13} />
      </button>
      {pop.open && (
        <div
          className="popover popover-right topbar-more-menu"
          role="dialog"
          aria-label="More"
          {...pop.panelProps}
        >
          <button
            type="button"
            className="account-item"
            onClick={() => {
              pop.setOpen(false);
              onOpenPalette();
            }}
          >
            <Icon name="search" size={14} />
            <span className="account-item-label">Command palette</span>
            <span className="muted">Ctrl+K</span>
          </button>
          {foldTags && (
            <div className="more-status" role="group" aria-label="Status">
              <span
                className="statusline-tag"
                data-tag="hotkeys"
                data-on={hotkeys.on ? "1" : "0"}
                title={hotkeys.title}
              >
                {hotkeys.label}
              </span>
              <span className="statusline-tag" data-tag="session" title={session.title}>
                {session.label}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * "Copy" (owner 2026-10-04): opens the Copy trader pop-out; its status reads OFF, or ON · the
 * active group while the gateway copies. It reads only the gateway's copy state, which changes
 * when copying is switched or reconfigured, never per tick.
 */
function CopyButton(): React.JSX.Element {
  const copy = useTrading((t) => t.copy);
  const s = copyTopBarStatus(copy);
  return (
    <button
      type="button"
      className="topbar-copy"
      data-tone={s.tone}
      aria-haspopup="dialog"
      aria-label={`Copy trader: ${s.label}`}
      title={`${s.title}. Open the copy trader.`}
      onClick={openCopyTrader}
    >
      <Icon name="copy" size={13} />
      <span className="topbar-copy-name">Copy</span>
      <span className="topbar-copy-state">{s.label}</span>
    </button>
  );
}

/**
 * "Chart only" (owner 2026-10-05): the workspace shows its charts only (workspace/chartOnly.ts);
 * the status line, the position strip and Flatten stay. On, it is pressed and reads
 * "Chart only · on".
 */
function ChartOnlyButton(): React.JSX.Element {
  const on = useChartOnly();
  return (
    <button
      type="button"
      className={on ? "statusline-btn chart-only-btn active" : "statusline-btn chart-only-btn"}
      aria-pressed={on}
      title={
        on
          ? "Chart only is on: only the charts show. Click to show every module again."
          : "Chart only: hide the DOM, order pad, accounts and the other modules; the charts fill the workspace. Position, P&L and Flatten stay."
      }
      onClick={toggleChartOnly}
    >
      <Icon name="chart" size={13} />
      <span className="statusline-btn-word">Chart only</span>
      {on && <span className="chart-only-state">· on</span>}
    </button>
  );
}

/**
 * The user chip at the far right opens the account menu: who is signed in, and Sign out. Two
 * clicks away and at the opposite end from Flatten, so it is not hit by accident while trading.
 */
function AccountMenu({ me }: { me: Me }): React.JSX.Element {
  const pop = usePopover();
  return (
    <div className="popover-anchor">
      <button
        type="button"
        className="user-chip"
        aria-haspopup="dialog"
        aria-label="Account menu"
        title={`${me.email} · ${me.user_id}`}
        {...pop.triggerProps}
      >
        <Icon name="user" size={13} />
        <span className="user-email">{me.email}</span>
        <Icon name="chevronDown" size={12} />
      </button>
      {pop.open && (
        <div
          className="popover popover-right user-menu"
          role="dialog"
          aria-label="Account"
          {...pop.panelProps}
        >
          <div className="user-menu-who">
            <span className="account-group-title">Signed in as</span>
            <span className="user-menu-email">{me.email}</span>
          </div>
          <SignOutButton className="btn btn-block" />
        </div>
      )}
    </div>
  );
}

/** Network state dot (mobile header and tab bar; the desktop shows `ConnectionItem`). */
export function NetworkStatusIndicator({
  connection,
  compact = false,
}: {
  connection: ConnectionView;
  compact?: boolean;
}): React.JSX.Element {
  const s = networkStatus(connection);
  return (
    <span className={`net net-${s.kind}`} role="status" aria-live="polite" title={s.detail}>
      <span className="net-dot" aria-hidden="true" />
      {compact ? <span className="sr-only">{s.label}</span> : <span className="net-label">{s.label}</span>}
    </span>
  );
}

/** "Lockout": the Personal Lockout panel (the palette's "Personal Lockout" opens it too). */
function LockoutButton({ lockedUntil }: { lockedUntil: number | null }): React.JSX.Element {
  const pop = usePopover({ keepOpenOnAlertDialog: true });
  const { setOpen } = pop;
  useEffect(
    () =>
      onOpenLockoutPanel(() => {
        setOpen(true);
      }),
    [setOpen],
  );
  const locked = lockedUntil !== null && lockedUntil > Date.now();
  return (
    <div className="popover-anchor">
      <button
        type="button"
        className={locked ? "statusline-btn lockout-btn locked" : "statusline-btn lockout-btn"}
        aria-haspopup="dialog"
        aria-label="Personal Lockout"
        title={locked ? "Personal Lockout active" : "Personal Lockout"}
        {...pop.triggerProps}
      >
        <Icon name="lock" size={13} />
        <span className="statusline-btn-word">Lockout</span>
      </button>
      {pop.open && (
        <div
          className="popover popover-right lockout-popover"
          role="dialog"
          aria-label="Personal Lockout"
          {...pop.panelProps}
        >
          <LockoutPanel lockedUntil={lockedUntil} />
        </div>
      )}
    </div>
  );
}
